import { FC, useMemo } from "react";
import { FooterLinks } from "./FooterLinks";
import { FOOTER_LINKS } from "./constants";
import { Logo } from "../Logo";
import { useGetContactsQuery } from "src/redux/api";
import { ReactComponent as PhoneIcon } from "src/assets/icons/phone.svg";
import { ReactComponent as MailIcon } from "src/assets/icons/mail.svg";
import { ReactComponent as Facebookcon } from "src/assets/icons/facebook.svg";
import { ReactComponent as MarkerIcon } from "src/assets/icons/marker.svg";
import { ReactComponent as StarIcon } from "src/assets/icons/star.svg";
import { ReactComponent as StarWayIcon } from "src/assets/icons/star-way-icon.svg";
import { PATHNAMES } from "src/constants/routes";
import { IFooterLink } from "./types";

interface Props {
  isShown?: boolean;
}

const formatPhoneForTel = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, "");
  return cleaned.startsWith("38") ? `+${cleaned}` : `+38${cleaned}`;
};

export const Footer: FC<Props> = ({ isShown = true }) => {
  const { data: contacts } = useGetContactsQuery();

  const footerLinks = useMemo<IFooterLink[]>(() => {
    if (!contacts) return FOOTER_LINKS;

    const contactLinks: IFooterLink = {
      id: 3,
      links: [
        {
          id: 1,
          label: contacts.phone,
          href: `tel:${formatPhoneForTel(contacts.phone)}`,
          icon: PhoneIcon,
        },
        {
          id: 2,
          label: contacts.email,
          href: `mailto:${contacts.email}`,
          icon: MailIcon,
        },
        {
          id: 3,
          label: contacts.facebook.includes("facebook.com") 
            ? contacts.facebook.split("facebook.com/")[1]?.split("/")[0] || contacts.facebook
            : contacts.facebook,
          href: contacts.facebook.startsWith("http") ? contacts.facebook : `https://${contacts.facebook}`,
          icon: Facebookcon,
          isOpenNewTab: true,
        },
      ],
    };

    const addressLink: IFooterLink = {
      id: 4,
      links: [
        {
          id: 1,
          label: contacts.address,
          href: "https://www.google.com/maps/search/%D0%B2%D1%83%D0%BB.+%D0%9C%D0%B8%D0%BA%D0%BE%D0%BB%D0%B8+%D0%93%D0%BE%D0%B3%D0%BE%D0%BB%D1%8F,+5+%D0%BC.+%D0%A2%D0%B5%D1%80%D0%BD%D0%BE%D0%BF%D1%96%D0%BB%D1%8C,+46001+%D0%A3%D0%BA%D1%80%D0%B0%D1%97%D0%BD%D0%B0/@49.5506626,25.5970939,17z/data=!3m1!4b1?entry=ttu&g_ep=EgoyMDI0MDkyNS4wIKXMDSoASAFQAw%3D%3D",
          icon: MarkerIcon,
          isOpenNewTab: true,
        },
        {
          id: 2,
          label:
            "вул. Томашевського, 6/1 м. Львів, Україна",
          href: "https://maps.app.goo.gl/wt3dgHAUe5kDccYR9",
          icon: MarkerIcon,
          isOpenNewTab: true,
        },
        {
          id: 3,
          label: contacts.slogan,
          href: PATHNAMES.HOME,
          icon: StarIcon,
        },
        {
          id: 4,
          label: "Created by Starway",
          href: "https://starway.agency/",
          icon: StarWayIcon,
          isOpenNewTab: true,
        },
      ],
    };

    return [
      FOOTER_LINKS[0],
      FOOTER_LINKS[1],
      contactLinks,
      addressLink,
    ];
  }, [contacts]);

  if (!isShown) return null;

  return (
    <footer className="bg-green-base rounded-t-3xl">
      <div className="container flex flex-wrap xs:justify-between gap-5 py-6 lg:py-10">
        <Logo className="!h-15 lg:!h-25" />

        {footerLinks.map(({ id, links }) => (
          <FooterLinks key={id} links={links} />
        ))}
      </div>
    </footer>
  );
};
